/**
 * A copy of src/lib/adminProduct.js in lumiere-mobile, kept identical so the
 * app and the website edit a product the same way.
 *
 * Editing a product from the admin — the shapes, kept out of the screen so
 * they can be tested, because this is what gets written to the live catalogue.
 *
 * PATCH /admin/products/:id (backend/server.js) accepts: name, description,
 * top/middle/base notes, is_active, is_featured, price_30/50/100,
 * stock_30/50/100 and sale_price_100. It updates existing size variants and
 * never creates one, and it has no way to change brand, category, gender or
 * concentration — so neither does the form.
 */

/* MySQL hands back DECIMAL and SUM() as text ("119.00"), so every number from
   the list is read through this. Absent stays absent: a size the product
   does not have is null, not 0. */
export const num = v => (v == null || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null);
const str = v => (num(v) == null ? '' : String(num(v)));

export const SIZES = [30, 50, 100];

/** An admin list row, as an edit form. */
export const fromProduct = p => ({
  id: p.id,
  name: p.name || '',
  brand: p.brand || p.brand_name || '',
  cat: p.category || '',
  gender: p.gender || '',
  conc: p.concentration || '',
  top: p.top_notes || '',
  mid: p.middle_notes || '',
  base: p.base_notes || '',
  desc: p.description || '',
  p30: str(p.price_30),
  p50: str(p.price_50),
  p100: str(p.price_100),
  sale: str(p.sale_price),
  s30: str(p.stock_30),
  s50: str(p.stock_50),
  s100: str(p.stock_100),
  /* Only sizes that exist can be edited: PATCH updates variants, it does not
     create them. */
  has: SIZES.filter(ml => num(p[`price_${ml}`]) != null),
  active: p.is_active == null ? true : !!Number(p.is_active),
  featured: !!Number(p.is_featured),
  thumb: p.thumbnail_url || null,
});

/** Total stock for the list's warning pill. The admin list calls it total_stock. */
export const stockOf = p => num(p && (p.total_stock != null ? p.total_stock : p.stock));

/**
 * What is wrong with an edit, if anything, as an i18n key — checked before
 * anything is sent, because the server silently ignores a zero price rather
 * than rejecting it.
 */
export const checkEdit = form => {
  if (!String(form.name || '').trim()) return { key: 'admin.nameRequired' };
  for (const ml of form.has) {
    if (!(num(form[`p${ml}`]) > 0)) return { key: 'admin.priceInvalid', ml };
  }
  if (form.has.includes(100) && form.sale !== '' && !(num(form.sale) > 0 && num(form.sale) < num(form.p100))) {
    return { key: 'admin.saleInvalid' };
  }
  return null;
};

/** The PATCH body for an edit form. */
export const patchBodyFor = form => {
  const body = {
    name: String(form.name).trim(),
    description: form.desc,
    top_notes: form.top,
    middle_notes: form.mid,
    base_notes: form.base,
    is_active: form.active ? 1 : 0,
    is_featured: form.featured ? 1 : 0,
  };
  form.has.forEach(ml => {
    body[`price_${ml}`] = form[`p${ml}`];
    /* An emptied stock field is left out rather than sent: the server would
       read '' as 0 and mark the size sold out. */
    if (form[`s${ml}`] !== '') body[`stock_${ml}`] = form[`s${ml}`];
  });
  /* The server checks the sale against price_100 from the same request, so
     it goes alongside it; an emptied field clears the sale. */
  if (form.has.includes(100)) body.sale_price_100 = form.sale === '' ? null : form.sale;
  return body;
};
